/**
 * Taxability of Social Security benefits (spec §9).
 *
 * Up to 85% of benefits become taxable based on PROVISIONAL income
 * (AGI excluding SS + tax-exempt interest + half of SS benefits), via a
 * two-tier threshold calculation.
 *
 * CRITICAL: the provisional-income thresholds are NOT indexed to inflation and
 * have been fixed since the 1980s/1990s. Do NOT inflate them. In a multi-decade
 * projection an increasing share of benefits becomes taxable purely from
 * nominal growth — and this drives the "tax torpedo" (spec §9).
 */
import type { FilingStatus } from '../../model/types';
import { federalTaxYear } from '../data/federal';

export interface SsTaxabilityResult {
  provisionalIncome: number;
  taxableBenefits: number;
  taxablePercent: number; // taxableBenefits / grossBenefits (0 when gross is 0)
}

/**
 * @param otherIncomeExclSS  all income except SS (ordinary + STCG + QDI + LTCG)
 * @param taxExemptInterest  muni-bond interest etc. (added back for provisional)
 * @param grossSsBenefits    total Social Security benefits received in the year
 */
export function taxableSocialSecurity(
  year: number,
  filingStatus: FilingStatus,
  otherIncomeExclSS: number,
  taxExemptInterest: number,
  grossSsBenefits: number,
): SsTaxabilityResult {
  const fy = federalTaxYear(year);
  const t1 = fy.socialSecurityTaxability.tier1[filingStatus];
  const t2 = fy.socialSecurityTaxability.tier2[filingStatus];

  const provisionalIncome = otherIncomeExclSS + taxExemptInterest + 0.5 * grossSsBenefits;

  if (grossSsBenefits <= 0) {
    return { provisionalIncome, taxableBenefits: 0, taxablePercent: 0 };
  }

  let taxable: number;
  if (provisionalIncome <= t1) {
    taxable = 0;
  } else if (provisionalIncome <= t2) {
    // Tier 1: lesser of 50% of benefits or 50% of the excess over tier1.
    taxable = Math.min(0.5 * grossSsBenefits, 0.5 * (provisionalIncome - t1));
  } else {
    // Tier 2: 85% of the excess over tier2, plus the lesser of the tier-1
    // amount or 50% of (tier2 - tier1); capped at 85% of benefits.
    const tier1Amount = Math.min(0.5 * grossSsBenefits, 0.5 * (t2 - t1));
    taxable = Math.min(
      0.85 * grossSsBenefits,
      0.85 * (provisionalIncome - t2) + tier1Amount,
    );
  }

  taxable = Math.max(0, Math.min(taxable, 0.85 * grossSsBenefits));
  return {
    provisionalIncome,
    taxableBenefits: taxable,
    taxablePercent: taxable / grossSsBenefits,
  };
}
